import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ToastTypes, useMessage } from '../../../../../base/context/MessageProvider';
import { useDispatch, useSelector } from 'react-redux';
import { CategoryApiService } from '../../../../../service/website/categoryApiService';
import { addItems, categoryData } from '../../../../../redux/slices/categorySlice';
import { backIcon, closeIcon, nextVIcon } from '../../../../assets/icons';
import Logo from '../../../../assets/images/common/logo.png';
import { queryToUrl, setUrl, getFeaturedCategoryPath } from '../../../../helper/utils';
import { navbarTopMenu } from '../../../../helper/constants';
import logger from '../../../../helper/logger';
import { safeGetCategories, safeGetCName, safeRenderCategoryList } from '../../../../../helper/dataSanitizer';// slug helper reused from product utils for consistent cleaning
import { slugifyTitle } from '../../../../../utils/productUrlUtils'; import './style.css';

export default function MenuBar({ menu, setMenu, featuredCategories }) {
  const location = useLocation();
  const message = useMessage();
  const dispatch = useDispatch();

  const [showCategories, setShowCategories] = useState(false);

  const categories = useSelector(categoryData);

  // lock/unlock background scroll when sidebar menu toggles
  useEffect(() => {
    if (menu) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [menu]);

  useEffect(() => {
    setMenu(false);
  }, [location]);

  useEffect(() => {
    if (!location.pathname.includes('/product-details') && !location.pathname.includes('/p/')) handleCategories();
  }, []);

  const handleCategories = () => {
    // Initial fetch: only root categories (lazy)
    CategoryApiService.getCategories(null, true)
      .then(res => {
        if (res.status.code === 200) {
          dispatch(addItems(res.data));
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch(err => {
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      });
  };

  return (
    <div>
      <div className={!menu ? 'navbar_category_menu_container' : 'navbar_category_menu_container_show'} onClick={() => setMenu(false)}></div>

      <div className={!menu ? 'navbar_category_menu_before' : 'navbar_category_menu_after'}>
        <div className="navbar_category_menu_header">
          <img src={Logo} />
          <div onClick={() => setMenu(false)}>{closeIcon}</div>
        </div>

        {!showCategories ? (
          <>
            <div className="navbar_pages_row_on_mobile">
              {navbarTopMenu.map((menu, index) => {
                return (
                  <div key={index}>
                    <Link to={menu.link} className="category_menu_item">
                      <p>{menu.name}</p>
                    </Link>
                  </div>
                );
              })}
            </div>

            <div className="navbar_pages_row_on_mobile">
              {featuredCategories?.map((menu, index) => {
                return (
                  <div key={index}>
                    <Link to={getFeaturedCategoryPath(menu.pageUrl) + `?id=${menu._id}`} className="category_menu_item">
                      <p>{menu.tagName}</p>
                    </Link>
                  </div>
                );
              })}
            </div>

            {/* Alibaba Category */}
            <div className="navbar_pages_row_on_mobile">
              <div>
                <Link to="/collection?category=global" className="category_menu_item">
                  <p>Global</p>
                </Link>
              </div>
            </div>

            <div className="navbar_pages_row_on_mobile">
              <p className="all_category_menu" onClick={() => setShowCategories(true)}>
                All Categories {nextVIcon}{' '}
              </p>
            </div>
          </>
        ) : null}

        {showCategories && categories ? <Categories categories={categories} setShowCategories={setShowCategories} /> : null}

        {categories ? (
          <div className="category_on_lg_screen">
            <Categories categories={categories} setShowCategories={setShowCategories} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

const Categories = ({ categories, setShowCategories }) => {
  const [data, setData] = useState(safeGetCategories(categories));
  const [parentData, setParentData] = useState([]);
  const [activeParent, setActiveParent] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const message = useMessage();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (parentData.length) {
      setActiveParent(parentData[parentData.length - 1]);
    }
  }, [parentData]);

  useEffect(() => {
    // Use safe category data
    const validCategories = safeGetCategories(categories);
    setData(validCategories);
    setParentData([]);
    setActiveParent('');
  }, [location]);

  const handleData = e => {
    const categoryName = safeGetCName(e);
    if (categoryName === 'Unknown Category') {
      console.error('Invalid category data:', e);
      return;
    }

    if (e.children && e.children.length) {
      setData(safeGetCategories(e.children));
      setParentData([...parentData, e]);
    } else if (e.hasChildren) {
      // Lazy load child categories
      CategoryApiService.getCategories(e._id || e.c_id, true)
        .then(res => {
          if (res.status.code === 200) {
            const children = safeGetCategories(res.data);
            e.children = children; // Attach loaded children to the parent object
            setData(children);
            setParentData([...parentData, e]);
          } else {
            message.showToast(res.error.message, ToastTypes.ERROR);
          }
        })
        .catch(err => {
          message.showToast(err?.message || 'Error loading subcategories', ToastTypes.ERROR);
        });
    } else {
      viewProduct(categoryName, e);
    }
  };

  const handleParent = e => {
    const categoryName = safeGetCName(e);
    if (categoryName === 'Unknown Category') {
      console.error('Invalid parent category data:', e);
      return;
    }

    const index = parentData.findIndex(item => safeGetCName(item) === categoryName);
    const newParentData = parentData.filter(item => safeGetCName(item) !== categoryName);

    if (index === 0) {
      setData(categories);
      setActiveParent('');
      setParentData([]);
    } else if (index > 0) {
      setData(parentData[index - 1].children);
      setActiveParent(parentData[index - 1]);
      setParentData(newParentData);
    }
  };

  const viewProduct = (name, selectedCategory = null) => {
    // Build array of category names (parents + clicked leaf)
    const parentNames = parentData
      .map(p => safeGetCName(p))
      .filter(n => n && n !== 'Unknown Category');
    const pathParts = [...parentNames];
    if (name) {
      const leafName = safeGetCName({ c_name: name }) || name;
      if (leafName && leafName !== 'Unknown Category') {
        pathParts.push(leafName);
      }
    }

    setShowCategories(false);

    if (pathParts.length) {
      // convert each segment to URL-friendly slug
      const slugParts = pathParts.map(p => slugifyTitle(p));
      const selectedCategoryId = selectedCategory?.c_id || selectedCategory?.id || selectedCategory?._id || null;
      // include 'category' prefix for clarity
      const categoryQuery = selectedCategoryId ? `?cid=${encodeURIComponent(String(selectedCategoryId))}` : '';
      navigate(`/category/${slugParts.join('/')}${categoryQuery}`);
    }
  };

  return (
    <div>
      {activeParent && safeGetCName(activeParent) !== 'Unknown Category' ? (
        <div className="menubar_active_parent">
          <div onClick={() => handleParent(activeParent)}>
            {backIcon} {safeGetCName(activeParent)}
          </div>
          <span onClick={() => viewProduct('', activeParent)}>View All</span>
        </div>
      ) : (
        <p className="back_to_main" onClick={() => setShowCategories(false)}>
          {backIcon} <span>Back to main menu</span>
        </p>
      )}

      {safeRenderCategoryList(data, (item, index) => (
        <div key={index} className="menubar_item" onClick={() => handleData(item)}>
          <span>{safeGetCName(item)}</span>
          {nextVIcon}
        </div>
      ))}
    </div>
  );
};
